import React from "react";
import { ShieldCheck, Zap, FileCheck, Layers, Clock, X, Check } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { useReveal } from "../../hooks/useReveal";

const reasonIcons = [Zap, FileCheck, ShieldCheck, Layers];

export const PayFac = () => {
  const { t } = useLanguage();
  const [ref, visible] = useReveal();
  const p = t.payfac;

  return (
    <section id="payfac" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <span className="inline-block text-[11px] font-bold uppercase tracking-wider text-[#0fb013] bg-[#0fe814]/15 px-3 py-1.5 rounded-full">
              {p.label}
            </span>
            <h2 className="mt-5 font-extrabold text-[28px] sm:text-[36px] lg:text-[42px] leading-tight tracking-tight text-black">
              {p.title}
            </h2>
            <p className="mt-5 text-[16px] leading-relaxed text-black/60 font-medium">{p.desc}</p>
            <p className="mt-4 text-[15px] leading-relaxed text-black/60 font-medium">{p.desc2}</p>

            <div
              ref={ref}
              className={`mt-9 grid sm:grid-cols-2 gap-4 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
            >
              {p.reasons.map((r, i) => {
                const Icon = reasonIcons[i];
                return (
                  <div key={i} data-testid={`payfac-reason-${i}`} className="flex gap-3" style={{ transitionDelay: visible ? `${i * 60}ms` : "0ms" }}>
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-black flex items-center justify-center">
                      <Icon size={18} className="text-[#0fe814]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[14px] text-black leading-snug">{r.title}</h3>
                      <p className="mt-1 text-[13px] leading-relaxed text-black/55 font-medium">{r.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid md:grid-cols-2 gap-5">
              <div data-testid="payfac-traditional" className="rounded-3xl border border-black/[0.08] bg-[#fafcf9] p-7 lg:p-8">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[12px] font-bold uppercase tracking-wider text-black/45">{p.traditional.label}</span>
                  <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-black/60 whitespace-nowrap">
                    <Clock size={14} /> {p.traditional.time}
                  </span>
                </div>
                <ul className="mt-6 space-y-4">
                  {p.traditional.steps.map((s, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-black/[0.06] flex items-center justify-center">
                        <X size={12} className="text-black/40" strokeWidth={3} />
                      </span>
                      <span className="text-[14px] leading-relaxed text-black/60 font-medium">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div data-testid="payfac-rapidpay" className="relative overflow-hidden rounded-3xl bg-black text-white p-7 lg:p-8">
                <div className="absolute top-[-100px] right-[-100px] w-[260px] h-[260px] rounded-full bg-[#0fe814]/20 blur-3xl pointer-events-none" />
                <div className="relative flex items-center justify-between gap-3">
                  <span className="text-[12px] font-bold uppercase tracking-wider text-[#0fe814]">{p.rapidpay.label}</span>
                  <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-black bg-[#0fe814] px-3 py-1 rounded-full whitespace-nowrap">
                    <Zap size={13} /> {p.rapidpay.time}
                  </span>
                </div>
                <ul className="relative mt-6 space-y-4">
                  {p.rapidpay.steps.map((s, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-[#0fe814] flex items-center justify-center">
                        <Check size={12} className="text-black" strokeWidth={3} />
                      </span>
                      <span className="text-[14px] leading-relaxed text-white font-medium">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-5 rounded-3xl border border-black/[0.08] bg-white p-6 lg:p-7">
              <p className="text-[13px] font-bold uppercase tracking-wider text-black/45">{p.how.label}</p>
              <p className="mt-2 text-[14px] leading-relaxed text-black/65 font-medium">{p.how.desc}</p>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                {p.how.chain.map((c, i) => (
                  <React.Fragment key={c}>
                    <span className={`text-[12px] font-bold px-3.5 py-2 rounded-full ${i === 1 ? "bg-[#0fe814] text-black" : "bg-black/[0.05] text-black/70"}`}>{c}</span>
                    {i < p.how.chain.length - 1 && <span className="text-black/30 font-bold">→</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PayFac;
